import { useEffect } from "react"
import Icon from "../../components/Icon"
import { formatMoney } from "../../utils/format"

// Purpose-built confirmation for auto-invoicing. It explains that the setting
// is already saved even though this month's bills continue in the background.
function AutoInvoiceSuccessModal({
  categoryName,
  providerName,
  amount,
  dayOfMonth,
  run,
  onClose,
}) {
  useEffect(() => {
    const handleKey = (event) => {
      if (event.key === "Escape") onClose?.()
    }
    document.addEventListener("keydown", handleKey)
    return () => document.removeEventListener("keydown", handleKey)
  }, [onClose])

  const alreadyProcessed = run?.status === "completed"

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center px-4">
      <div
        className="absolute inset-0 bg-slate-900/55 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="auto-invoice-success-title"
        className="relative z-10 w-full max-w-sm overflow-hidden rounded-3xl bg-white shadow-2xl"
      >
        <div className="bg-gradient-to-br from-emerald-50 via-white to-teal-50 px-6 pb-5 pt-7 text-center">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500 text-white shadow-lg shadow-emerald-200">
            <Icon name="check-lg" size="1.7rem" />
          </div>
          <h3 id="auto-invoice-success-title" className="mt-4 text-xl font-black text-slate-900">
            Auto-invoicing is on
          </h3>
          <p className="mt-1 text-sm text-slate-600">Your monthly billing setting was saved.</p>
        </div>

        <div className="space-y-4 px-5 pb-5">
          <div className="grid grid-cols-2 gap-2 rounded-2xl border border-slate-100 bg-slate-50 p-3 text-left">
            <div className="col-span-2">
              <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Category</p>
              <p className="truncate text-sm font-black text-slate-800">{categoryName || "Utility bill"}</p>
              {providerName && <p className="truncate text-[11px] text-slate-500">{providerName}</p>}
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Monthly amount</p>
              <p className="text-sm font-black text-slate-800">KSh {formatMoney(amount)}</p>
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Billing day</p>
              <p className="text-sm font-black text-slate-800">Day {dayOfMonth}</p>
            </div>
          </div>

          <div className="flex gap-3 rounded-2xl border border-teal-100 bg-teal-50 px-3.5 py-3 text-left">
            <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-teal-600 text-white">
              <Icon name={alreadyProcessed ? "calendar-check" : "arrow-repeat"} size="0.8rem" />
            </span>
            <div>
              <p className="text-xs font-black text-teal-900">
                {alreadyProcessed ? "This month is already complete" : "This month's bills are being prepared"}
              </p>
              <p className="mt-0.5 text-[11px] leading-relaxed text-teal-700">
                {alreadyProcessed
                  ? "This month was already processed. Your updated setting will be used for future bills."
                  : "Bills are being created safely in the background. You can close this message and continue using RecordLOGS."}
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            autoFocus
            className="w-full rounded-xl bg-teal-600 py-3 text-sm font-black text-white transition-colors hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-300 focus:ring-offset-2"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  )
}

export default AutoInvoiceSuccessModal
